import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './styles/design-system.css';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import Callback from './Callback';
import Admin from './Admin';
import BlogDetail from './BlogDetail';
import BlogsPage from './pages/BlogsPage';
import OffersPage from './pages/OffersPage';
import OfferDetailPage from './pages/OfferDetailPage';
import OfferCategoryPage from './pages/OfferCategoryPage';
import StorePage from './pages/StorePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFound from './pages/NotFound';
import SavedPage from './pages/SavedPage';
import BottomNav from './components/common/BottomNav';
import ThemeToggle from './components/common/ThemeToggle';
import ScrollToTopButton from './components/common/ScrollToTopButton';
import OfferCard from './components/common/OfferCard';
import { OFFER_CATEGORIES } from './constants/categories';
import { STORE_LINKS } from './constants/stores';
import { useWishlist } from './utils/wishlist';
import { imageFallback } from './utils/env';
import { toSlug } from './utils/slug';
import { OffersProvider, useOffers } from './context/OffersContext';

// Ensure affiliate links always have a protocol so they don't resolve as relative URLs
function ensureProtocol(link) {
  if (!link || link === '#') return '#';
  if (/^https?:\/\//i.test(link)) return link;
  return `https://${link}`;
}

// Module-level constant — avoids re-creating the array on every render
// Flipkart-style category nav icons — keyed by category label
const CATEGORY_ICONS = {
  All: '🛍️',
  Shopping: '🛒',
  Electronics: '💻',
  Fashion: '👗',
  Food: '🍔',
  'Card Offer': '💳',
  Travel: '✈️',
  Others: '📦',
};

// Per-history-entry scroll memory. Module-level so it survives component unmounts
// but resets on full page reload (which is what the user expects).
const scrollPositions = new Map();

// Disable browser's built-in scroll restoration — we manage it ourselves
// so it doesn't race against React's render and content-visibility layout.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Scroll manager. Ordering matters here — React runs all useLayoutEffect cleanups
// (in hook declaration order) BEFORE any new useLayoutEffect body. So the save
// MUST be in a useLayoutEffect cleanup, declared BEFORE the restore effect, so it
// captures the real scrollY before the restore effect changes it.
function ScrollManager() {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const savedYRef = useRef(null);

  // (1) Save scroll BEFORE any other effect changes it.
  useLayoutEffect(() => {
    return () => {
      scrollPositions.set(key, window.scrollY);
    };
  }, [key]);

  // (2) Restore scroll synchronously before paint (POP) or jump to top (PUSH/REPLACE).
  useLayoutEffect(() => {
    if (navigationType === 'POP') {
      const saved = scrollPositions.get(key);
      savedYRef.current = saved ?? null;
      if (saved != null) window.scrollTo(0, saved);
    } else {
      savedYRef.current = null;
      window.scrollTo(0, 0);
    }
  }, [key, navigationType]);

  // (3) Re-apply on the next two frames in case lazy images / content-visibility
  // change document height shortly after mount, causing the initial scrollTo to clamp.
  useEffect(() => {
    if (savedYRef.current == null) return;
    const target = savedYRef.current;
    const raf1 = requestAnimationFrame(() => {
      window.scrollTo(0, target);
      requestAnimationFrame(() => window.scrollTo(0, target));
    });
    return () => cancelAnimationFrame(raf1);
  }, [key, navigationType]);

  return null;
}

function MainApp({ focusSection = null }) {
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useWishlist();
  const {
    items, lastEvaluatedKey, hasMore, filter, search, activeSearch,
    loadingOffers, loadingMore, offersError,
    trendingItems, loadingFeatured,
    setFilter, setSearch, setActiveSearch,
    fetchOffers,
  } = useOffers();

  // Carousel slide is local — no need to persist across navigation.
  // Keeping it in context caused every consumer (incl. all offer cards) to
  // re-render every 5s when the auto-rotate timer ticked.
  const [currentSlide, setCurrentSlide] = useState(0);

  function handleSearch(e) {
    e.preventDefault();
    setActiveSearch(search.trim());
  }

  function clearSearch() {
    setSearch('');
    setActiveSearch('');
  }

  const sentinelRef = useRef(null);
  const filterChangeAt = useRef(Date.now());

  // Reset cooldown timer on every filter/search change so auto-load-more
  // is suppressed for ~800ms while the new page is settling.
  useEffect(() => {
    filterChangeAt.current = Date.now();
  }, [filter, activeSearch]);

  useEffect(() => {
    if (!sentinelRef.current) return;

    function maybeLoadMore() {
      if (Date.now() - filterChangeAt.current < 800) return;
      if (!hasMore || loadingMore || loadingOffers) return;
      const el = sentinelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Same 600px look-ahead the observer used, but checked directly against
      // the viewport on every scroll tick — IntersectionObserver can miss a
      // callback during fast continuous scrolling to the very bottom of the
      // page, which otherwise required scrolling back up to "unstick" it.
      if (rect.top <= window.innerHeight + 600) {
        fetchOffers(filter, activeSearch, lastEvaluatedKey);
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() - filterChangeAt.current < 800) return;
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loadingOffers) {
          fetchOffers(filter, activeSearch, lastEvaluatedKey);
        }
      },
      { threshold: 0, rootMargin: '600px 0px' }
    );
    observer.observe(sentinelRef.current);

    // Fallback scroll/resize listener, throttled via rAF, to guarantee the
    // load-more check runs even if the IntersectionObserver callback doesn't
    // fire in time during fast scrolling.
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        maybeLoadMore();
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Run once immediately in case the sentinel is already in view (e.g. short lists)
    maybeLoadMore();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [hasMore, loadingMore, loadingOffers, lastEvaluatedKey, filter, activeSearch, fetchOffers]);

  // Auto-slide — only when items exist
  useEffect(() => {
    if (trendingItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % trendingItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [trendingItems.length, setCurrentSlide]);

  // Touch swipe support
  const touchStartX = useRef(null);
  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) < 40) return; // ignore small taps
    if (diff > 0) {
      // swiped left → next
      setCurrentSlide((prev) => (prev + 1) % trendingItems.length);
    } else {
      // swiped right → prev
      setCurrentSlide((prev) => (prev - 1 + trendingItems.length) % trendingItems.length);
    }
    touchStartX.current = null;
  }


  // "Categories" and "Hot Deals" in the bottom nav are real routes that render
  // this page focused on a section. The scroll waits for the target to actually
  // exist (the trending banner mounts only once its fetch resolves) rather than
  // guessing with a setTimeout, and defers to ScrollManager on Back/Forward.
  const categoryNavRef = useRef(null);
  const trendingRef = useRef(null);
  const focusedRef = useRef(null);
  const focusNavigationType = useNavigationType();

  useEffect(() => {
    if (!focusSection) {
      focusedRef.current = null;
      return;
    }
    if (focusNavigationType === 'POP') return;
    if (focusedRef.current === focusSection) return;
    const el = focusSection === 'categories' ? categoryNavRef.current : trendingRef.current;
    if (!el) return; // Not mounted yet — a later dep change re-runs this.
    focusedRef.current = focusSection;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusSection, focusNavigationType, loadingFeatured, trendingItems.length, loadingOffers]);

  const types = ['All', ...OFFER_CATEGORIES];

  const isLoading = loadingOffers || loadingFeatured;

  return (
    <div className="App">
      <Helmet>
        <title>{focusSection === 'categories'
          ? 'Shop Offers by Category — Electronics, Fashion, Mobiles & More | OfferLelo'
          : focusSection === 'hot-deals'
          ? "Hot Deals Today — Trending Flipkart, Amazon & Myntra Offers | OfferLelo"
          : 'OfferLelo — Flipkart, Amazon, Myntra, Ajio Offers & Deals Today in India'}</title>
        <meta name="description" content="Find today's best Flipkart offers, Amazon deals, Myntra coupons &amp; Ajio discounts in India. Verified offers on Electronics, Fashion, Mobiles, Food &amp; Travel — updated daily with OfferLelo!" />
        <meta name="keywords" content="flipkart offer, amazon offer, myntra offer, ajio offer, flipkart deals today, amazon deals today, myntra coupons, ajio discount, online shopping offers India, best deals India, electronics offers, fashion deals, mobile offers, credit card offers, cashback offers" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta property="og:title" content="OfferLelo — Flipkart, Amazon, Myntra, Ajio Offers & Deals Today in India" />
        <meta property="og:description" content="Find today's best Flipkart, Amazon, Myntra & Ajio offers & deals in India. Verified discounts on Electronics, Fashion, Mobiles, Food & Travel — updated daily." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.offerlelo.com/" />
        <meta property="og:image" content="https://www.offerlelo.com/logo192.png" />
        <meta property="og:site_name" content="OfferLelo" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OfferLelo — Flipkart, Amazon, Myntra, Ajio Offers & Deals Today" />
        <meta name="twitter:description" content="Today's best Flipkart, Amazon, Myntra & Ajio offers & deals in India — verified daily." />
        <meta name="twitter:image" content="https://www.offerlelo.com/logo192.png" />
        <link rel="canonical" href="https://www.offerlelo.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.offerlelo.com/#organization",
              "name": "OfferLelo",
              "alternateName": ["Offer Lelo", "OfferLeleo"],
              "url": "https://www.offerlelo.com",
              "logo": { "@type": "ImageObject", "url": "https://www.offerlelo.com/logo192.png" },
              "description": "OfferLelo brings you today's best Flipkart, Amazon, Myntra and Ajio offers, deals and coupons in India.",
              "areaServed": { "@type": "Country", "name": "India" },
              "sameAs": []
            },
            {
              "@type": "WebSite",
              "@id": "https://www.offerlelo.com/#website",
              "url": "https://www.offerlelo.com",
              "name": "OfferLelo",
              "description": "Best Flipkart, Amazon, Myntra & Ajio offers, deals & coupons in India — updated daily.",
              "inLanguage": "en-IN",
              "publisher": { "@id": "https://www.offerlelo.com/#organization" },
              "potentialAction": {
                "@type": "SearchAction",
                "target": { "@type": "EntryPoint", "urlTemplate": "https://www.offerlelo.com/offers?search={search_term_string}" },
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@type": "WebPage",
              "@id": "https://www.offerlelo.com/#webpage",
              "url": "https://www.offerlelo.com/",
              "name": "OfferLelo — Flipkart, Amazon, Myntra, Ajio Offers & Deals Today in India",
              "isPartOf": { "@id": "https://www.offerlelo.com/#website" },
              "about": { "@id": "https://www.offerlelo.com/#organization" },
              "description": "Find today's best Flipkart, Amazon, Myntra & Ajio offers & deals in India. Verified discounts on Electronics, Fashion, Mobiles, Food & Travel — updated daily.",
              "inLanguage": "en-IN"
            },
            {
              "@type": "ItemList",
              "name": "Top Stores with Offers on OfferLelo",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Flipkart Offers", "url": "https://www.offerlelo.com/store/flipkart" },
                { "@type": "ListItem", "position": 2, "name": "Amazon Offers",   "url": "https://www.offerlelo.com/store/amazon" },
                { "@type": "ListItem", "position": 3, "name": "Myntra Offers",   "url": "https://www.offerlelo.com/store/myntra" },
                { "@type": "ListItem", "position": 4, "name": "Ajio Offers",     "url": "https://www.offerlelo.com/store/ajio" }
              ]
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.offerlelo.com/" }
              ]
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Where can I find today's best Flipkart offers?",
                  "acceptedAnswer": { "@type": "Answer", "text": "OfferLelo lists today's verified Flipkart offers, deals and coupons across mobiles, electronics, fashion and home — updated daily." }
                },
                {
                  "@type": "Question",
                  "name": "Does OfferLelo show Amazon, Myntra and Ajio offers too?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes. OfferLelo aggregates the latest Amazon deals, Myntra coupons and Ajio discounts in India alongside Flipkart offers, all on one page." }
                },
                {
                  "@type": "Question",
                  "name": "Are the offers on OfferLelo verified?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every offer on OfferLelo is hand-curated and verified before being listed. Prices and availability may change on the retailer's website." }
                }
              ]
            }
          ]
        })}</script>
      </Helmet>
      {isLoading && <div className="top-loading-bar" />}
      <a href="#main-content" className="skip-link">Skip to content</a>
      <header className="modern-header">
        <div className="header-logo">
          <img src="/logo.svg" alt="OfferLelo Logo" />
          <span className="header-logo-text">OfferLelo</span>
        </div>
        <div className="header-actions">
          <ThemeToggle />
          <div className="blogs-nav-btn-wrap">
            <button className="blogs-nav-btn" onClick={() => navigate('/blogs')}>
              <span className="blogs-nav-btn-icon">📋</span>
              <span>Blogs</span>
            </button>
          </div>
        </div>
      </header>

      {/* Flipkart-style category icon nav */}
      <nav className="category-nav-wrap" aria-label="Browse by category" ref={categoryNavRef}>
        <div className="category-nav">
          {types.map(type => (
            <button
              key={type}
              className={filter === type ? 'category-nav-item active' : 'category-nav-item'}
              onClick={() => (type === 'All' ? setFilter('All') : navigate(`/offers/${toSlug(type)}`))}
            >
              <span className="category-nav-icon">{CATEGORY_ICONS[type] || '🏷️'}</span>
              <span className="category-nav-label">{type}</span>
            </button>
          ))}
        </div>
      </nav>

      <div id="main-content" />
      <h1 style={{ fontSize: '0.92rem', fontWeight: 600, color: '#64748b', lineHeight: 1.4, maxWidth: 1100, margin: '14px auto 2px', padding: '0 16px' }}>
        Best Flipkart, Amazon, Myntra &amp; Ajio Offers Today in India
      </h1>
      {/* Sliding Trending Banner */}
      {loadingFeatured && (
        <div className="skeleton-banner">
          <div className="skeleton-banner-inner">
            <div className="skeleton-dark skeleton-banner-img" />
            <div className="skeleton-banner-text">
              <div className="skeleton-dark skeleton-banner-line short" style={{ width: '28%', height: 22, borderRadius: 20 }} />
              <div className="skeleton-dark skeleton-banner-line wide" />
              <div className="skeleton-dark skeleton-banner-line mid" />
              <div className="skeleton-dark skeleton-banner-line short" />
            </div>
          </div>
        </div>
      )}
      {!loadingFeatured && trendingItems.length > 0 && (
        <div className="trending-banner" ref={trendingRef}>
          <div className="trending-slider" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            {trendingItems.map((item, index) => (
              <a
                key={item._uid || item.sk || item.id || index}
                href={ensureProtocol(item.link)}
                target="_blank"
                rel="noopener noreferrer"
                className={`trending-card ${index === currentSlide ? 'active' : ''}`}
                style={{ display: index === currentSlide ? 'flex' : 'none' }}
              >
                <div className="trending-card-img-wrap">
                  <img
                    src={item.image || '/default-offer.svg'}
                    alt={item.title}
                    className="trending-card-img"
                    width="800"
                    height="320"
                    decoding="async"
                    loading={index === currentSlide ? 'eager' : 'lazy'}
                    fetchpriority={index === currentSlide ? 'high' : 'low'}
                    onError={imageFallback('/default-offer.svg')}
                  />
                  <span className="trending-card-badge">🔥 HOT</span>
                  {trendingItems.length > 1 && (
                    <div className="trending-dots">
                      {trendingItems.map((_, dotIndex) => (
                        <span
                          key={dotIndex}
                          className={`dot ${dotIndex === currentSlide ? 'active' : ''}`}
                          onClick={(e) => { e.preventDefault(); setCurrentSlide(dotIndex); }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="trending-card-info">
                  <div className="trending-card-title">{item.title}</div>
                </div>
              </a>
            ))}
          </div>
          {trendingItems.length > 1 && (
            <>
              <button
                type="button"
                className="slider-arrow left"
                aria-label="Previous offer"
                onClick={() => setCurrentSlide((prev) => (prev - 1 + trendingItems.length) % trendingItems.length)}
              >&#8249;</button>
              <button
                type="button"
                className="slider-arrow right"
                aria-label="Next offer"
                onClick={() => setCurrentSlide((prev) => (prev + 1) % trendingItems.length)}
              >&#8250;</button>
            </>
          )}
        </div>
      )}

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="search-bar-form">
        <div className="search-bar-input-wrap">
          <span className="search-bar-icon" aria-hidden="true">🔍</span>
          <input
            type="text"
            placeholder="Search offers, stores or products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-bar-input"
            aria-label="Search offers"
          />
          {search && (
            <button type="button" onClick={clearSearch} className="search-bar-clear-btn" aria-label="Clear search">
              ✕
            </button>
          )}
        </div>
        <button type="submit" className="search-bar-submit-btn">
          Search
        </button>
      </form>
      {activeSearch && (
        <div className="search-active-banner">
          Showing results for <strong>"{activeSearch}"</strong>
          <button onClick={clearSearch} className="search-active-clear-link">
            Clear
          </button>
        </div>
      )}

      {/* Shop by Store — dedicated store-landing entry points (SEO + UX) */}
      {!activeSearch && (
        <section className="store-section" aria-label="Shop by Store">
          <h2 className="store-section-heading">
            <span className="store-section-heading-fire">🔥</span>
            Shop by Store
            <span className="store-section-live-badge">
              ● LIVE
            </span>
          </h2>
          <div className="store-grid">
            {STORE_LINKS.map(s => (
              <a
                key={s.slug}
                className="store-card"
                href={`/store/${s.slug}`}
                title={`${s.name} Offers — ${s.sub}`}
                aria-label={`Browse ${s.name} offers — ${s.sub}`}
                onClick={e => { e.preventDefault(); navigate(`/store/${s.slug}`); }}
              >
                <span
                  className="store-card-icon"
                  style={{ background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}b3 100%)` }}
                >
                  {s.name.charAt(0)}
                </span>
                <span className="store-card-name">{s.name}</span>
                <span className="store-card-sub" style={{ background: `${s.color}1a`, color: s.color }}>{s.sub}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      <div className="main-content-wrap">
        {loadingOffers && (
          <div className="grid-container">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="skeleton-card" key={i}>
                <div className="skeleton skeleton-card-img" />
                <div className="skeleton-card-body">
                  <div className="skeleton skeleton-card-line wide" />
                  <div className="skeleton skeleton-card-line mid" />
                  <div className="skeleton skeleton-card-line short" />
                </div>
              </div>
            ))}
          </div>
        )}
        {offersError && (
          <div className="state-banner state-banner-error">
            <span className="state-banner-icon" aria-hidden="true">⚠️</span>
            <p>Something went wrong: {offersError}</p>
            <button type="button" className="state-banner-retry-btn" onClick={() => fetchOffers(filter, activeSearch)}>
              Try again
            </button>
          </div>
        )}
        {!loadingOffers && !offersError && (
          <>
            <div className="grid-container">
              {items.length === 0 ? (
                <div className="no-items">
                  <span className="no-items-icon" aria-hidden="true">🔍</span>
                  <p>No offers found{activeSearch ? ` for "${activeSearch}"` : ''}.</p>
                  {(activeSearch || filter !== 'All') && (
                    <button type="button" className="no-items-reset-btn" onClick={() => { clearSearch(); setFilter('All'); }}>
                      Reset filters
                    </button>
                  )}
                </div>
              ) : (
                items.map(item => (
                  <OfferCard
                    key={item.id}
                    item={item}
                    savedIds={savedIds}
                    onToggleSave={toggleSave}
                  />
                ))
              )}
            </div>
            <div ref={sentinelRef} style={{ height: 32 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: '#6366f1', fontWeight: 600, fontSize: '0.95rem' }}>
                Loading...
              </div>
            )}
          </>
        )}

        {/* Blog CTA Banner */}
        <div className="blog-cta-banner" onClick={() => navigate('/blogs')}>
          <div className="blog-cta-left">
            <div className="blog-cta-icon">✍️</div>
            <div>
              <div className="blog-cta-tag">Expert Guides &amp; Tips</div>
              <h2 className="blog-cta-title">Read Our Latest Blogs</h2>
              <p className="blog-cta-subtitle">Shopping guides, deal breakdowns &amp; money-saving tips — updated daily</p>
              <div className="blog-cta-chips">
                {['👗 Fashion', '📱 Electronics', '🏠 Home', '🥗 Food & Health', '📲 Mobile'].map(chip => (
                  <span key={chip} className="blog-cta-chip">{chip}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="blog-cta-right">
            <div className="blog-cta-stats-row">
              <div className="blog-cta-stat-item">
                <span className="blog-cta-stat-n">100+</span>
                <span className="blog-cta-stat-l">Articles</span>
              </div>
              <div className="blog-cta-stat-sep" />
              <div className="blog-cta-stat-item">
                <span className="blog-cta-stat-n">6</span>
                <span className="blog-cta-stat-l">Topics</span>
              </div>
              <div className="blog-cta-stat-sep" />
              <div className="blog-cta-stat-item">
                <span className="blog-cta-stat-n">Free</span>
                <span className="blog-cta-stat-l">Always</span>
              </div>
            </div>
            <button className="blog-cta-btn" onClick={e => { e.stopPropagation(); navigate('/blogs'); }}>
              <span>Explore Blogs</span>
              <span className="blog-cta-btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4c1d95 100%)', color: '#c7d2fe', marginTop: 40, padding: '48px 24px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Top grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 36, marginBottom: 40 }}>

            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <img src="/logo.svg" alt="OfferLelo" style={{ width: 32, height: 32 }} />
                <span style={{ fontSize: '1.4rem', fontWeight: 800, background: 'linear-gradient(90deg, #fff 0%, #fde68a 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>OfferLelo</span>
              </div>
              <p style={{ fontSize: '0.88rem', lineHeight: 1.7, color: '#a5b4fc', margin: 0 }}>
                Your one-stop destination for the best deals, discounts, and offers across top brands. Save more on every purchase.
              </p>
            </div>

            {/* Offer Categories */}
            <div>
              <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 14, fontSize: '1rem' }}>Offer Categories</h4>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { icon: '🛍️', label: 'Shopping', desc: 'Best deals from top stores' },
                  { icon: '💳', label: 'Card Offers', desc: 'Cashback & credit card deals' },
                  { icon: '📱', label: 'Electronics', desc: 'Mobiles, laptops & gadgets' },
                  { icon: '👗', label: 'Fashion', desc: 'Clothing, footwear & accessories' },
                  { icon: '🍔', label: 'Food', desc: 'Restaurant & delivery discounts' },
                  { icon: '✈️', label: 'Travel', desc: 'Flights, hotels & holiday packages' },
                ].map(({ icon, label, desc }) => (
                  <li key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}
                    onClick={() => navigate('/')}>
                    <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>{icon}</span>
                    <div>
                      <div style={{ color: '#e0e7ff', fontWeight: 600, fontSize: '0.85rem' }}>{label}</div>
                      <div style={{ color: '#818cf8', fontSize: '0.75rem' }}>{desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 14, fontSize: '1rem' }}>Quick Links</h4>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { icon: '🏠', label: 'Home', action: () => navigate('/') },
                  { icon: '📝', label: 'Blogs', action: () => navigate('/blogs') },
                  { icon: '🔥', label: 'Trending Deals', action: () => navigate('/') },
                  { icon: '⭐', label: 'Featured Offers', action: () => navigate('/') },
                  { icon: '🔒', label: 'Privacy Policy', action: () => navigate('/privacy-policy') },
                  { icon: '📋', label: 'Terms of Service', action: () => navigate('/terms') },
                ].map(({ icon, label, action }) => (
                  <li key={label}>
                    <button onClick={action}
                      style={{ background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer', fontSize: '0.88rem', padding: 0, display: 'flex', alignItems: 'center', gap: 6, transition: 'color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                      onMouseLeave={e => e.currentTarget.style.color = '#a5b4fc'}
                    >
                      <span>{icon}</span> {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Us */}
            <div>
              <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 14, fontSize: '1rem' }}>Contact Us</h4>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>📧</span>
                  <div>
                    <div style={{ color: '#e0e7ff', fontWeight: 600, fontSize: '0.82rem', marginBottom: 2 }}>Email Us</div>
                    <a
                      href="mailto:offerleleo365@gmail.com"
                      style={{ color: '#a5b4fc', textDecoration: 'none', fontSize: '0.8rem', transition: 'color 0.2s', wordBreak: 'break-all' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#fde68a'}
                      onMouseLeave={e => e.currentTarget.style.color = '#a5b4fc'}
                    >
                      offerleleo365@gmail.com
                    </a>
                  </div>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>🌐</span>
                  <div>
                    <div style={{ color: '#e0e7ff', fontWeight: 600, fontSize: '0.82rem', marginBottom: 2 }}>Website</div>
                    <span style={{ color: '#a5b4fc', fontSize: '0.8rem' }}>www.offerlelo.com</span>
                  </div>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>⏰</span>
                  <div>
                    <div style={{ color: '#e0e7ff', fontWeight: 600, fontSize: '0.82rem', marginBottom: 2 }}>Response Time</div>
                    <span style={{ color: '#a5b4fc', fontSize: '0.8rem' }}>Within 24 hours</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Why OfferLelo */}
            <div>
              <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 14, fontSize: '1rem' }}>Why OfferLelo?</h4>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { icon: '✅', text: 'Verified & curated deals daily' },
                  { icon: '🔒', text: 'Safe & trusted affiliate links' },
                  { icon: '⚡', text: 'Real-time offer updates' },
                  { icon: '🎯', text: 'Deals across all categories' },
                  { icon: '📖', text: 'Expert buying guides & blogs' },
                ].map(({ icon, text }) => (
                  <li key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.85rem', color: '#a5b4fc' }}>
                    <span style={{ flexShrink: 0 }}>{icon}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 20, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#818cf8' }}>
              © {new Date().getFullYear()} OfferLelo. All rights reserved. &nbsp;·&nbsp;
              <a href="mailto:offerleleo365@gmail.com" style={{ color: '#a5b4fc', textDecoration: 'none' }}>Contact</a>
              &nbsp;·&nbsp;
              <button onClick={() => navigate('/privacy-policy')} style={{ background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer', fontSize: '0.82rem', padding: 0, textDecoration: 'none' }}>Privacy Policy</button>
              &nbsp;·&nbsp;
              <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer', fontSize: '0.82rem', padding: 0, textDecoration: 'none' }}>Terms</button>
            </p>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#4f46e5' }}>
              Prices and offers are subject to change. Please verify on the retailer's website.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  if (window.location.hostname === 'offerlelo.com') {
    window.location.replace('https://www.offerlelo.com' + window.location.pathname + window.location.search);
    return null;
  }
  return (
    <BrowserRouter>
      <ScrollManager />
      <OffersProvider>
        <Routes>
          <Route path="/" element={<MainApp />} />
          <Route path="/categories" element={<MainApp focusSection="categories" />} />
          <Route path="/hot-deals" element={<MainApp focusSection="hot-deals" />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blog/:categorySlug/:slug" element={<BlogDetail />} />
          <Route path="/offers" element={<OffersPage />} />
          {/* Single-segment category hub (/offers/electronics). Must stay
              alongside the two-segment detail route below — before this
              existed, every category URL fell through to NotFound. */}
          <Route path="/offers/:typeSlug" element={<OfferCategoryPage />} />
          <Route path="/offers/:typeSlug/:slug" element={<OfferDetailPage />} />
          <Route path="/store/:storeSlug" element={<StorePage />} />
          <Route path="/saved" element={<SavedPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <BottomNav />
        <ScrollToTopButton />
      </OffersProvider>
    </BrowserRouter>
  );
}

export default App;
